import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";

/**
 * Manual compliance verification after STUB / incomplete FMCSA results.
 */
export default function ManualVerifyModal({
  checkTitle,
  onClose,
  onConfirm,
}) {
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (status) => {
    setSaving(true);
    setError("");
    try {
      await onConfirm({ status, notes: notes.trim() || undefined });
    } catch (err) {
      setError(err?.message || "Verification failed");
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40 px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-md space-y-fluid-4 rounded-2xl bg-white p-fluid-4 shadow-xl dark:bg-acs-dark-surface"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8EFF3] text-[#223688]">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h2 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Manual Verification
              </h2>
              <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext">
                {checkTitle || "Compliance check"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-fluid-3 rounded-lg border border-gray-200 bg-gray-50 p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-hover">
          <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
            Confirm you have independently verified this carrier (authority,
            safety rating, etc.). This records a human decision until live FMCSA
            is functional.
          </p>
          <div>
            <label className="mb-1 block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
              Notes (optional)
            </label>
            <textarea
              rows={3}
              value={notes}
              disabled={saving}
              onChange={(e) => {
                setNotes(e.target.value);
                if (error) setError("");
              }}
              placeholder="e.g. Verified on FMCSA website — authority active"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-fluid-sm dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text"
            />
            {error ? (
              <p className="mt-1 text-fluid-sm text-red-600">{error}</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-fluid-2 border-t border-gray-200 pt-fluid-2 dark:border-acs-dark-border">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-gray-300 px-fluid-button-x py-fluid-button-y text-fluid-sm text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => submit("FAIL")}
            className="rounded-full border border-[#B7660C] px-fluid-button-x py-fluid-button-y text-fluid-sm font-semibold text-[#B7660C] hover:bg-[#FBF0DF] disabled:opacity-50"
          >
            Mark Failed
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => submit("PASS")}
            className="rounded-full bg-[#223688] px-fluid-button-x py-fluid-button-y text-fluid-sm font-semibold text-white hover:bg-[#1E2A74] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Mark Verified"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
